import React, { useState, useEffect, useMemo, useRef } from "react";
import { trackError } from "../../../../lib/metrics";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { ModalRegister } from "../../../../components/ui/ModalRegister";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import { getInspectKeys } from "../../../../data/inspectables";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import MiragioCaptchaModal from "../../../origami/components/ui/MiragioCaptchaModal";
import MiragioCaptchaSolutionSheet from "../../../origami/components/ui/MiragioCaptchaSolutionSheet";
import HintsModalHub from "../../../../components/ui/HintsModalHub";

const STAGE2_REAL = process.env.REACT_APP_STAGE2_REAL === "true";
const API = process.env.REACT_APP_API_URL;

function PlaceholderStage2_Player1() {
  return (
    <div>
      <h2>🧪 Étape 2 — Joueur 1 - Version Bêta (placeholder)</h2>
      <p className="mt-2">Cette page est <strong>temporaire</strong> pour la bêta.</p>
      <p className="mt-4 text-sm text-slate-500">Si tu restes bloqué(e) : rafraîchis la page (F5).</p>
    </div>
  );
}

async function apiGetAvailableLayers(code) {
  const res = await fetch(`${API}/api/sessions/${encodeURIComponent(code)}/layers`);
  if (!res.ok) throw new Error(`GET layers failed (${res.status})`);
  return res.json();
}

async function apiUnlockBox(code, playerId) {
  const res = await fetch(`${API}/api/sessions/${encodeURIComponent(code)}/layers/unlock-box`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerId: Number(playerId) }),
  });
  if (!res.ok) throw new Error(`unlock-box failed (${res.status})`);
  return res.json();
}

function clampRot(r) {
  const x = ((r % 360) + 360) % 360;
  const snaps = [0, 90, 180, 270];
  let best = snaps[0]; let bestD = Infinity;
  for (const s of snaps) { const d = Math.abs(s - x); if (d < bestD) { bestD = d; best = s; } }
  return best;
}

const SEGMENTS_BY_LAYER = {
  1: [[70, 60, 220, 60], [220, 60, 220, 220], [70, 220, 220, 220], [70, 60, 70, 220]],
  2: [[260, 70, 450, 70], [260, 140, 450, 140], [260, 210, 450, 210]],
  3: [[120, 95, 200, 175], [200, 175, 280, 95], [280, 95, 360, 175], [360, 175, 440, 95]],
  4: [[95, 140, 235, 140], [235, 140, 235, 250], [235, 250, 425, 250]],
  5: [[110, 40, 110, 120], [410, 40, 410, 120], [110, 120, 410, 120], [260, 120, 260, 240]],
};

function layerColorClass(id) {
  const map = { 1: "text-cyan-300", 2: "text-fuchsia-300", 3: "text-amber-300", 4: "text-emerald-300", 5: "text-indigo-300" };
  return map[id] || "text-slate-200";
}

function LayerShapePreview({ layerId }) {
  const segs = SEGMENTS_BY_LAYER[layerId] || [];
  const centerX = 260; const centerY = 140;
  return (
    <svg viewBox="0 0 520 280" className="w-full h-full">
      <g className={layerColorClass(layerId)} transform={`rotate(${clampRot(0)} ${centerX} ${centerY})`} opacity={0.98}
        style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.35)) drop-shadow(0 0 10px rgba(0,0,0,0.22))" }}>
        {segs.map((s, i) => (
          <line key={`${layerId}-${i}`} x1={s[0]} y1={s[1]} x2={s[2]} y2={s[3]}
            stroke="currentColor" strokeWidth="6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    </svg>
  );
}

function LayerFoundPage({ layerId }) {
  return (
    <div className="flex flex-col items-center gap-5 py-6">
      <div className="text-sm uppercase tracking-[0.2em] text-slate-400">Élément récupéré - Fragment {layerId}</div>
      <div className="w-[320px] h-[320px] bg-black/40 border border-white/10 rounded-xl p-4 flex items-center justify-center">
        <LayerShapePreview layerId={layerId} />
      </div>
      <div className="text-lg font-semibold text-white">Vous avez trouvé un fragment de validation.</div>
    </div>
  );
}

function parseEquation(str) {
  const m = String(str).match(/^(\d+)\+(\d+)\+(\d+)=(\d+)$/);
  if (!m) return null;
  const [_, a, b, c, s] = m;
  return { a: Number(a), b: Number(b), c: Number(c), s: Number(s) };
}

function isEquationCorrect(str) {
  const p = parseEquation(str);
  if (!p) return false;
  return p.a + p.b + p.c === p.s;
}

function DigitSwapEquationControlled({ title, initial, chars, setChars, solved, setSolved, onSolved, onReset }) {
  const initialChars = useMemo(() => Array.from(initial), [initial]);
  const [dragIdx, setDragIdx] = useState(null);

  const greenIdxs = useMemo(() => {
    if (!solved) return new Set();
    const s = new Set();
    for (let i = 0; i < chars.length; i++) { if (chars[i] !== initialChars[i]) s.add(i); }
    return s;
  }, [solved, chars, initialChars]);

  const trySolve = (nextChars) => {
    const nextStr = nextChars.join("");
    if (!isEquationCorrect(nextStr)) return;
    const greens = [];
    for (let i = 0; i < nextChars.length; i++) {
      if (nextChars[i] !== initialChars[i] && /\d/.test(nextChars[i])) greens.push(nextChars[i]);
    }
    if (greens.length !== 2) return;
    setSolved(true);
    onSolved?.(greens.join(""));
  };

  const swap = (i, j) => {
    if (solved) return;
    if (i == null || j == null || i === j) return;
    if (!/\d/.test(chars[i]) || !/\d/.test(chars[j])) return;
    const next = chars.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setChars(next);
    trySolve(next);
  };

  return (
    <div className="w-full">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{title}</div>
      <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-4">
        <div className="flex flex-wrap gap-1 items-center justify-center select-none">
          {chars.map((ch, idx) => {
            const isDigit = /\d/.test(ch);
            const isNeon = greenIdxs.has(idx);
            return (
              <span key={idx} draggable={isDigit && !solved}
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => { if (isDigit && !solved) e.preventDefault(); }}
                onDrop={() => { swap(dragIdx, idx); setDragIdx(null); }}
                className={["inline-flex items-center justify-center w-8 h-10 rounded-lg text-base font-mono text-gray-200",
                  isDigit ? "cursor-grab" : "cursor-default",
                  isDigit ? "border border-white/15 bg-white/5" : "text-gray-200",
                  solved && isDigit ? "cursor-default" : ""].join(" ")}
                style={isNeon ? { background: "#39ff14", color: "#08110a", borderColor: "#39ff14", boxShadow: "0 0 14px rgba(57,255,20,0.85), inset 0 0 10px rgba(0,0,0,0.25)" } : undefined}
              >{ch}</span>
            );
          })}
        </div>
        {solved && <div className="mt-3 text-center text-sm font-semibold text-slate-400 uppercase"><span style={{ color: "#39ff14" }}>Cohérence restaurée</span></div>}
        {!solved && <div className="mt-3 text-center text-xs text-slate-400">Astuce : glisse un chiffre sur un autre pour les échanger.</div>}
        <button type="button" onClick={() => { if (solved) return; setChars(Array.from(initial)); setSolved(false); onReset?.(); }}
          className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-slate-200 text-xs hover:bg-white/10 transition">Reset</button>
      </div>
    </div>
  );
}

function CodeEntryModal({ open, onClose, expected, disabled, onSuccess, onHint, setShowHintModal, onWrongCode }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);

  useEffect(() => { if (open) { setCode(""); setErr(null); setFlash(null); } }, [open]);

  const shake = () => { setFlash("bad"); setTimeout(() => setFlash(null), 260); };
  const submit = () => {
    if (disabled) { shake(); return; }
    if (code.trim() === expected) { setFlash("ok"); setTimeout(() => onSuccess?.(), 220); }
    else {
      setErr("Accès refusé.");
      shake();
      onWrongCode?.();  // callback vers le parent qui appelle trackError
    }
  };
  const press = (d) => { if (disabled) { shake(); return; } setErr(null); setCode((prev) => (prev + String(d)).slice(0, 4)); };
  const back = () => { setErr(null); setCode((prev) => prev.slice(0, -1)); };
  const clear = () => { setErr(null); setCode(""); };
  const dots = useMemo(() => Array.from({ length: 4 }, (_, i) => (i < code.length ? "●" : "○")).join(" "), [code]);

  return (
    <ModalRegister open={open} title="Boîte — panneau de sécurité" onClose={onClose} containerClassName="!max-w-[720px]" onHint={() => setShowHintModal(true)}>
      <div className="py-5">
        <div className="rounded-2xl border border-white/10 overflow-hidden shadow-[0_35px_120px_rgba(0,0,0,0.65)] bg-gradient-to-b from-slate-950/90 to-black">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-slate-400">Coffre de validation</div>
              <div className="mt-1 text-sm text-slate-200">Module d'accès — saisie chiffrée</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded-full border border-white/10"
                style={{ background: flash === "ok" ? "#39ff14" : "rgba(239,68,68,0.0)", boxShadow: flash === "ok" ? "0 0 16px rgba(57,255,20,0.75)" : "none" }} />
              <div className="w-3.5 h-3.5 rounded-full border border-white/10"
                style={{ background: flash === "bad" ? "rgba(239,68,68,0.95)" : "rgba(0,0,0,0.0)", boxShadow: flash === "bad" ? "0 0 16px rgba(239,68,68,0.55)" : "none" }} />
            </div>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-6">
            <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Afficheur</div>
                  <div className={["mt-2 font-mono text-3xl tracking-[0.35em] text-slate-100", flash === "bad" ? "translate-x-[2px]" : ""].join(" ")}
                    style={{ transition: "transform 120ms ease", textShadow: "0 0 20px rgba(255,255,255,0.08)" }}>{dots}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/50 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Verrou</div>
                  <div className="mt-1 text-sm text-slate-200">{disabled ? "BLOQUÉ" : "ARMÉ"}</div>
                </div>
              </div>
              {err && <div className="mt-3 text-sm text-red-300">{err}</div>}
              {disabled && <div className="mt-3 text-xs text-slate-500">Le panneau ne répond qu'aux procédures complètes.</div>}
              <div className="mt-5 flex items-center gap-3">
                <button type="button" onClick={clear} className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-slate-200 text-sm hover:bg-white/10 transition">Effacer</button>
                <button type="button" onClick={back} className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-slate-200 text-sm hover:bg-white/10 transition">⌫</button>
                <div className="flex-1" />
                <button type="button" disabled={code.length !== 4 || disabled} onClick={submit}
                  className="px-4 py-2 rounded-xl bg-emerald-600/80 hover:bg-emerald-600 text-white disabled:opacity-40 transition">Valider</button>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/35 p-4">
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Clavier</div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button key={n} type="button" onClick={() => press(n)}
                    className="h-12 rounded-xl border border-white/10 bg-white/5 text-slate-100 font-mono text-lg hover:bg-white/10 active:scale-[0.99] transition">{n}</button>
                ))}
                <button type="button" onClick={() => press(0)} className="h-12 col-span-2 rounded-xl border border-white/10 bg-white/5 text-slate-100 font-mono text-lg hover:bg-white/10 active:scale-[0.99] transition">0</button>
                <button type="button" onClick={submit} className="h-12 rounded-xl border border-emerald-500/30 bg-emerald-600/20 text-emerald-100 font-semibold hover:bg-emerald-600/30 transition">OK</button>
              </div>
              <div className="mt-4 text-[11px] text-slate-500">* Entrée limitée à 4 chiffres.</div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-white/10 flex justify-between">
            <button onClick={onClose} className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 transition">Fermer</button>
            <div className="text-xs text-slate-500">module: LOCK/VAULT · rev 2.1</div>
          </div>
        </div>
      </div>
    </ModalRegister>
  );
}

export default function Stage2Player1Room(props) {
  return STAGE2_REAL ? <RealStage2_Player1 {...props} /> : <PlaceholderStage2_Player1 />;
}

function RealStage2_Player1({
  code, playerId, hintSignal, raisedCount, raisedPlayers, centerMaxH, pseudo, playerCount,
}) {
  const INSPECT_KEYS = useMemo(() => {
    return getInspectKeys({ gameId: 1, playerCount: playerCount || 5, stage: 2, seat: 1 });
  }, [playerCount]);

  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const [bgReady, setBgReady] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);

  const { inspectedCount, total } = useInspectProgress({ sessionCode: code, stage: 2, playerId: Number(playerId), keys: INSPECT_KEYS });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 2 });
  const { unlockScenarioStep } = useScenarioTracker({ code });

  const ALPHA_INIT = "16+66+92=183";
  const BETA_INIT = "89+18+33=194";
  const [alphaChars, setAlphaChars] = useState(() => Array.from(ALPHA_INIT));
  const [betaChars, setBetaChars] = useState(() => Array.from(BETA_INIT));
  const [alphaSolved, setAlphaSolved] = useState(false);
  const [betaSolved, setBetaSolved] = useState(false);
  const [alphaPair, setAlphaPair] = useState(null);
  const [betaPair, setBetaPair] = useState(null);
  const [boxUnlockedByCode, setBoxUnlockedByCode] = useState(false);

  const [showAlpha, setShowAlpha] = useState(false);
  const [showBeta, setShowBeta] = useState(false);
  const [showOrderHint, setShowOrderHint] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showPostitMural, setShowPostitMural] = useState(false);
  // showBoiteSol supprimé — Initiative Tango retirée
  const [showTiroir, setShowTiroir] = useState(false);
  const [showServeur, setShowServeur] = useState(false);
  const [showClasseur, setShowClasseur] = useState(false);
  const [tiroirFileOpen, setTiroirFileOpen] = useState(null);
  const [showToxicPattern, setShowToxicPattern] = useState(false);

  const [fragmentStatus, setFragmentStatus] = useState({ 1: false, 2: false, 3: false, 4: false, 5: false });
  const expectedBoxCode = useMemo(() => (!alphaPair || !betaPair) ? null : `${alphaPair}${betaPair}`, [alphaPair, betaPair]);

  const [showMiragioCaptcha, setShowMiragioCaptcha] = useState(false);
  const [miragioCode, setMiragioCode] = useState(null); // eslint-disable-line no-unused-vars
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [availableLayers, setAvailableLayers] = useState([]);
  const [layersLoading, setLayersLoading] = useState(true); // eslint-disable-line no-unused-vars
  const prevLayersRef = useRef([]);
  const [newlyFoundLayers, setNewlyFoundLayers] = useState([]); // eslint-disable-line no-unused-vars
  const [boxPageIndex, setBoxPageIndex] = useState(0);

  const ALLOWED_LAYERS_FOR_THIS_PLAYER = useMemo(() => {
    return (playerCount <= 4) ? [1, 5] : [1];
  }, [playerCount]);

  const boxPages = useMemo(() => {
    const visibleLayers = (availableLayers || []).filter((id) => ALLOWED_LAYERS_FOR_THIS_PLAYER.includes(id));
    const ordered = [1, 5].filter((id) => visibleLayers.includes(id));
    return [...ordered.map((id) => ({ type: "layer", id })), { type: "sheet" }];
  }, [availableLayers, ALLOWED_LAYERS_FOR_THIS_PLAYER]);

  useEffect(() => { if (showBoxModal) setBoxPageIndex(0); }, [showBoxModal]);
  useEffect(() => { setBgReady(false); }, []);
  useEffect(() => { if (hintSignal > 0) setShowHintModal(true); }, [hintSignal]);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    let timer = null;
    const fetchLayers = async () => {
      try {
        const data = await apiGetAvailableLayers(code);
        const next = Array.isArray(data.availableLayers) ? data.availableLayers : [];
        if (cancelled) return;
        setAvailableLayers((prev) => {
          const a = (prev || []).join(","); const b = next.join(",");
          return a === b ? prev : next;
        });
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLayersLoading(false); }
    };
    setLayersLoading(true);
    fetchLayers();
    timer = setInterval(fetchLayers, 2000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [code]);

  useEffect(() => {
    const before = new Set(prevLayersRef.current || []);
    const diff = availableLayers.filter(id => !before.has(id));
    if (diff.length > 0) setNewlyFoundLayers(diff);
  }, [availableLayers]);

  useEffect(() => {
    setFragmentStatus({
      1: availableLayers.includes(1), 2: availableLayers.includes(2),
      3: availableLayers.includes(3), 4: availableLayers.includes(4),
      5: availableLayers.includes(5),
    });
  }, [availableLayers]);

  const openBox = async () => {
    setShowBoxModal(true);
    setBoxPageIndex(0);
    missionResolve({ key: "s2.p1.box.found" });
    const before = new Set(availableLayers || []);
    prevLayersRef.current = availableLayers || [];
    const data = await apiUnlockBox(code, playerId);
    const nextLayers = Array.isArray(data.availableLayers)
      ? data.availableLayers
      : (await apiGetAvailableLayers(code)).availableLayers;
    const safeNext = Array.isArray(nextLayers) ? nextLayers : [];
    setAvailableLayers(safeNext);
    const diff = safeNext.filter((id) => !before.has(id));
    setNewlyFoundLayers(diff);
    diff.filter((id) => id === 1 || id === 5).forEach((layerId) => {
      missionUnlock({ key: `s2.p1.layer${layerId}.found`, text: `${pseudo} a trouvé le fragment de validation ${layerId}.` });
    });
  };

  const backgroundUrl = "/assets/s2-p1-room.webp";

  const hintCategories = useMemo(() => [
    {
      id: "alpha", title: "Dossier ALPHA", subtitle: "Rapprochement comptable", hintKeyPrefix: "s2:p1:alpha",
      levels: {
        l1: (<><p className="mb-2">Le résultat 183 est correct.</p><p className="text-xs text-gray-300">Ce ne sont pas forcément deux chiffres adjacents, ni dans le même nombre.</p></>),
        l2: (<><p>16 est incorrect.</p></>),
        l3: (<><p>92 est incorrect.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Dossier ALPHA)</p><p className="mt-2 text-xs text-gray-200">Échanger le <span className="font-mono font-bold text-amber-200">1</span> de <span className="font-mono">16</span> avec le <span className="font-mono font-bold text-amber-200">2</span> de <span className="font-mono">92</span> donne :</p><p className="mt-1 font-mono text-xs text-emerald-300">26 + 66 + 91 = 183 ✓</p></>),
      },
    },
    {
      id: "beta", title: "Dossier BETA", subtitle: "Contrôle de conformité", hintKeyPrefix: "s2:p1:beta",
      levels: {
        l1: (<><p className="mb-2">Le résultat 194 est correct.</p><p className="text-xs text-gray-300">Ce ne sont pas forcément deux chiffres adjacents, ni dans le même nombre.</p></>),
        l2: (<><p>89 est incorrect.</p></>),
        l3: (<><p>33 est incorrect.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Dossier BETA)</p><p className="mt-2 text-xs text-gray-200">Échanger le <span className="font-mono font-bold text-amber-200">9</span> de <span className="font-mono">89</span> avec le premier <span className="font-mono font-bold text-amber-200">3</span> de <span className="font-mono">33</span> donne :</p><p className="mt-1 font-mono text-xs text-emerald-300">83 + 18 + 93 = 194 ✓</p></>),
      },
    },
    {
      id: "box", title: "Code de la boîte", subtitle: "Déverrouiller la boîte.", hintKeyPrefix: "s2:p1:box",
      levels: {
        l1: (<><p>La boîte refuse toute saisie tant qu'une condition préalable n'est pas remplie.</p></>),
        l2: (<><p>Les dossiers ALPHA et BETA contiennent chacun une information partielle, il faut les deux pour former le code.</p></>),
        l3: (<><p>L'ordre n'est pas arbitraire, un document dans la salle explique comment classer les "tirages".</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Code boîte)</p><p className="mt-2 text-xs text-gray-200">ALPHA (tirage 1) → chiffres verts : <span className="font-mono font-bold text-amber-200">21</span></p><p className="mt-1 text-xs text-gray-200">BETA (tirage 2) → chiffres verts : <span className="font-mono font-bold text-amber-200">39</span></p><p className="mt-2 font-mono text-sm text-emerald-300 font-bold">Code : 2139</p></>),
      },
    },
    {
      id: "captcha", title: "Captcha MIRAGIO", subtitle: "Orientation des fragments", hintKeyPrefix: "s2:p1:captcha",
      levels: {
        l1: (<><p className="mb-2">Le captcha ne demande pas un mot de passe, il demande une <span className="font-semibold">configuration visuelle</span>.</p><p className="text-xs text-gray-300">Il faut pour cela trouver les 5 fragments de validation.</p></>),
        l2: (<><p className="mb-2">Une feuille de référence de la forme cible existe quelque part dans la salle des archives.</p><p className="text-xs text-gray-300">Compare fragment par fragment : la couleur de chaque calque correspond à celle de la feuille.</p></>),
        l3: (<><p className="mb-2">Chaque fragment a une couleur unique. Sur la feuille, repère l'angle de chaque couleur et reproduis-le.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Captcha — orientations)</p><div className="mt-2 space-y-1 text-xs text-gray-200 font-mono"><p><span className="text-cyan-300">Fragment 1</span> (cyan) → <span className="text-amber-200 font-bold">90°</span></p><p><span className="text-fuchsia-300">Fragment 2</span> (fuchsia) → <span className="text-amber-200 font-bold">0°</span></p><p><span className="text-amber-300">Fragment 3</span> (ambre) → <span className="text-amber-200 font-bold">270°</span></p><p><span className="text-emerald-300">Fragment 4</span> (émeraude) → <span className="text-amber-200 font-bold">180°</span></p><p><span className="text-indigo-300">Fragment 5</span> (indigo) → <span className="text-amber-200 font-bold">90°</span></p></div></>),
      },
    },
    {
      id: "miragio_folder", title: "Dossier MIRAGIO", subtitle: "Déverrouillage dans le terminal", hintKeyPrefix: "s2:p1:miragio_folder",
      levels: {
        l1: (<><p className="mb-2">Le dossier MIRAGIO est dans les archives de l'ordinateur, verrouillé par un mot de passe.</p></>),
        l2: (<><p className="mb-2">Souviens-toi de ce que l'ordinateur a affiché quand le captcha a été validé.</p><p className="text-xs text-gray-300">Cette information est encore visible en haut de l'interface.</p></>),
        l3: (<><p className="mb-2">Le mot de passe figure dans le bandeau vert en haut du terminal, après validation du captcha.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Dossier MIRAGIO)</p><p className="mt-2 text-xs text-gray-200">Mot de passe : <span className="font-mono font-bold text-amber-200">OBSIDIAN</span></p></>),
      },
    },
    {
      id: "ingratitude_folder", title: "Dossier .ingratitude", subtitle: "Accès au protocole d'exécution", hintKeyPrefix: "s2:p1:ingratitude_folder",
      levels: {
        l1: (<><p className="mb-2">Ce dossier caché a son propre verrou, indépendant de MIRAGIO.</p><p className="text-xs text-gray-300">Le mot de passe ne vient pas de cette salle.</p></>),
        l2: (<><p className="mb-2">La calibration IA (salle de supervision) génère ce mot de passe une fois terminée.</p></>),
        l3: (<><p>La calibration IA nécessite la coopération de deux joueurs.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Dossier .ingratitude)</p><p className="mt-2 text-xs text-gray-200">Mot de passe : <span className="font-mono font-bold text-amber-200">EMPATH-4729</span></p></>),
      },
    },
  ], []);

  const TiroirContent = () => (
    <div className="py-4 px-2">
      {!tiroirFileOpen ? (
        <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5 space-y-4">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">🗂️ Contenu du tiroir</div>

          {/* Note aide directe sur le captcha */}
          <div className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-4">
            <div className="text-xs text-cyan-300 font-semibold mb-1">📌 Note — procédure accès MIRAGIO</div>
            <div className="text-sm text-slate-200 leading-relaxed">
              Lors de la validation du captcha de sécurité, un mot de passe s'affiche à l'écran.
              <span className="font-semibold text-cyan-200"> Il faut le noter immédiatement</span> — il sert à déverrouiller le dossier MIRAGIO dans le terminal.
            </div>
          </div>

          <div className="space-y-2 text-sm text-slate-300">
            <button onClick={() => setTiroirFileOpen("procedures")}
              className="w-full text-left rounded-lg border border-slate-700 bg-slate-800/40 p-3 hover:bg-slate-800/60 transition">
              <span className="text-slate-500">📁</span> "Procédures d'archivage 2024"
            </button>
          </div>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="text-xs text-amber-300 mb-2">📝 Post-it jaune :</div>
            <div className="text-sm text-slate-200 leading-relaxed">
              "Rappel : Vérifier cohérence des logs AVANT archivage. Si incohérence = bloquer l'accès jusqu'à correction."
            </div>
            <div className="text-xs text-slate-400 mt-3 italic">💡 Les dossiers ALPHA et BETA portent des annotations similaires.</div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
          <button onClick={() => setTiroirFileOpen(null)} className="text-xs text-cyan-400 mb-4 hover:underline">← Retour</button>
          {tiroirFileOpen === "procedures" && (
            <div className="space-y-3">
              <div className="text-sm font-mono text-slate-300">📄 procedures_archivage_2024.txt</div>
              <div className="rounded-lg border border-slate-700 bg-black/40 p-4 text-xs text-slate-300 font-mono leading-relaxed space-y-2">
                <p>PROCÉDURE STANDARD D'ARCHIVAGE</p>
                <p>Version 2024.1 — Département Archives</p>
                <p className="text-slate-500">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                <p>1. Vérification de cohérence des données</p>
                <p>   - Contrôle arithmétique obligatoire</p>
                <p>   - Validation croisée entre sources</p>
                <p className="text-slate-500">2. Classification par lots</p>
                <p className="text-slate-500">   - Numérotation séquentielle (tirage 1, 2...)</p>
                <p>3. Stockage sécurisé</p>
                <p>   - Classement alphabétique puis chronologique</p>
                <p>   - Coffre pour données sensibles</p>
                <p className="text-slate-500">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                <p className="text-amber-300">NOTE: Tout dossier incohérent doit être corrigé avant classement.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const ToxicPatternContent = () => (
    <div className="py-4 px-2">
      <div className="rounded-xl border border-purple-500/30 bg-slate-900/60 p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-purple-300 mb-4">📊 Analyse automatique — Canal interne #origami-support</div>
        <div className="rounded-lg border border-slate-700 bg-black/40 p-4 font-mono text-xs text-slate-300 space-y-2 mb-4">
          <p className="text-purple-300">ORIGAMI_ANALYTICS v3.1 — Rapport hebdomadaire</p>
          <p className="text-slate-500">━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
          <p>Canal analysé : <span className="text-cyan-300">#origami-support</span></p>
          <p className="text-amber-300">PATTERN DÉTECTÉ : Marqueurs [T-X]</p>
          <p>Fréquence : <span className="text-red-300">+340% vs semaine précédente</span></p>
        </div>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 mb-4">
          <div className="text-xs font-semibold text-amber-300 mb-2">🔍 Observation clé</div>
          <p className="text-sm text-slate-200 leading-relaxed">Les marquages <span className="font-mono font-bold text-amber-200">[T-X]</span> dans le canal <span className="font-mono text-cyan-300">#origami-support</span> indiquent des niveaux de toxicité numérotés dans l'ordre chronologique. Ces marqueurs forment une séquence utilisable comme code d'accès.</p>
        </div>
        <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
          <div className="text-xs text-red-300">⚠️ Communiquez cette information à l'équipe concernée.</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square" style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Salle des Archives
        </div>

        <img src={backgroundUrl} alt="Salle des Archives"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)} onError={() => setBgReady(true)} />

        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>

          <img src="/assets/computer.webp" alt="Ordinateur — accès sécurisé"
            onClick={async () => {
              markInspectedOnce('stage2player1:computer');
              missionUnlock({ key: "s2.p1.computer.found", text: `${pseudo} a trouvé un ordinateur verrouillé` });
              try {
                const data = await apiGetAvailableLayers(code);
                setAvailableLayers(Array.isArray(data.availableLayers) ? data.availableLayers : []);
              } catch (e) { console.error(e); }
              finally { setShowMiragioCaptcha(true); }
            }}
            className="s2p1-hotspot-computer absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(34,211,238,0.95)]" />

          <img src="/assets/box.webp" alt="Boîte verrouillée"
            onClick={async () => {
              markInspectedOnce('stage2player1:box');
              missionUnlock({ key: "s2.p1.box.found", text: `${pseudo} a trouvé une boîte verrouillée.` });
              try {
                if (!boxUnlockedByCode) { setShowCodeModal(true); return; }
                await openBox();
              } catch (e) { console.error(e); }
            }}
            className="s2p1-hotspot-box absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          <img src="/assets/dossier-alpha.webp" alt="Dossier ALPHA"
            onClick={() => { markInspectedOnce('stage2player1:dossierAlpha'); missionUnlock({ key: "s2.p1.alpha.found", text: `${pseudo} a trouvé le dossier ALPHA.` }); setShowAlpha(true); }}
            className="s2p1-hotspot-alpha absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          <img src="/assets/dossier-beta.webp" alt="Dossier BETA"
            onClick={() => { markInspectedOnce('stage2player1:dossierBeta'); missionUnlock({ key: "s2.p1.beta.found", text: `${pseudo} a trouvé le dossier BETA.` }); setShowBeta(true); }}
            className="s2p1-hotspot-beta absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          <img src="/assets/note-order.webp" alt="Note — ordre du code"
            onClick={() => { markInspectedOnce('stage2player1:noteOrder'); setShowOrderHint(true); }}
            className="s2p1-hotspot-order absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          <img src="/assets/postit-mural.webp" alt="Post-it — Procédure sécurité"
            onClick={() => { markInspectedOnce('stage2player1:postitMural'); setShowPostitMural(true); }}
            className="s2p1-hotspot-postit absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          {/* boite-sol / Initiative Tango supprimée */}

          <img src="/assets/tiroir.webp" alt="Tiroir — Dossiers admin"
            onClick={() => { markInspectedOnce('stage2player1:tiroir'); setShowTiroir(true); }}
            className="s2p1-hotspot-tiroir absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          <img src="/assets/serveur.webp" alt="Serveur — Diagnostic système"
            onClick={() => { markInspectedOnce('stage2player1:serveur'); setShowServeur(true); }}
            className="s2p1-hotspot-serveur absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          <img src="/assets/classeur.webp" alt="Classeur — Incidents critiques"
            onClick={() => { markInspectedOnce('stage2player1:classeur'); setShowClasseur(true); }}
            className="s2p1-hotspot-classeur absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]" />

          {playerCount <= 5 && (
            <img src="/assets/s2-p1-rapport-tx.png" alt="Rapport — canal #origami-support"
              onClick={() => { markInspectedOnce('stage2player1:rapportTX'); missionUnlock({ key: "s2.p1.tx.found", text: `${pseudo} a trouvé un rapport sur les marqueurs [T-X].` }); setShowToxicPattern(true); }}
              className="s2p1-hotspot-tx absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.85)]"
            />
          )}
        </div>

        {/* ── Modales ── */}

        <MiragioCaptchaModal
          playerId={playerId}
          open={showMiragioCaptcha} onClose={() => setShowMiragioCaptcha(false)}
          initialAvailable={availableLayers} pseudo={pseudo} code={code}
          playerCount={playerCount || 5} missionUnlock={missionUnlock}
          missionResolve={missionResolve} unlockScenarioStep={unlockScenarioStep}
          onSolved={(word) => { setMiragioCode(word); missionResolve({ key: "s2.p1.computer.found" }); }}
          onHint={() => setShowHintModal(true)} setShowHintModal={setShowHintModal}
        />

        <ModalRegister open={showBoxModal} title="Boîte — contenu" onClose={() => setShowBoxModal(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          {boxPages[boxPageIndex]?.type === "sheet" ? <MiragioCaptchaSolutionSheet /> : <LayerFoundPage layerId={boxPages[boxPageIndex]?.id} />}
          {boxPages.length > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <button disabled={boxPageIndex === 0} onClick={() => setBoxPageIndex((i) => Math.max(0, i - 1))} className="px-3 py-2 text-sm text-slate-200 disabled:opacity-30">← Précédent</button>
              <div className="text-xs text-slate-400">Page {boxPageIndex + 1}/{boxPages.length}</div>
              <button disabled={boxPageIndex === boxPages.length - 1} onClick={() => setBoxPageIndex((i) => Math.min(boxPages.length - 1, i + 1))} className="px-3 py-2 text-sm text-slate-200 disabled:opacity-30">Suivant →</button>
            </div>
          )}
        </ModalRegister>

        {/* Dossier ALPHA — indication équation ajoutée */}
        <ModalRegister open={showAlpha} title="Dossier ALPHA — Rapprochement comptable" onClose={() => setShowAlpha(false)} containerClassName="!max-w-[920px]" onHint={() => setShowHintModal(true)}>
          <div className="py-2">
            <div className="rounded-2xl border border-black/10 bg-[#f6f0e6] text-black shadow-[0_25px_80px_rgba(0,0,0,0.45)] overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-black/60">Archives — Ledger Patch (ALPHA)</div>
                    <div className="mt-1 text-[12px] text-black/70">Une ligne attire votre attention car le calcul semble incorrect.</div>
                  </div>
                  <div className="text-[11px] font-mono text-black/45">ref: FIN-ALPHA</div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px] text-black/70">
                  <div className="rounded-xl border border-black/10 bg-white/60 p-3">
                    <div className="font-semibold text-black/75">Feuillet de clôture</div>
                    <div className="mt-1">Rapprochement mensuel — centre de coûts "Archives". <span className="font-semibold">Deux chiffres semblent avoir été inversés.</span></div>
                  </div>
                  <div className="rounded-xl border border-black/10 bg-white/60 p-3">
                    <div className="font-semibold text-black/75">Marquages</div>
                    <div className="mt-2 flex flex-wrap gap-2 items-center">
                      <span className="px-2 py-1 rounded-md border border-black/15 bg-white/70 font-mono text-[11px]">FIN-ALPHA</span>
                      <span className="px-2 py-1 rounded-md border border-black/15 bg-white/70 font-mono text-[11px]" style={{ transform: "rotate(-2deg)" }}>tirage 1</span>
                      {alphaSolved && <span className="px-2 py-1 rounded-md border border-emerald-600/30 text-[11px]" style={{ background: "rgba(57,255,20,0.18)", boxShadow: "0 0 10px rgba(57,255,20,0.35)" }}>tampon : OK</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-4 rounded-xl border border-black/10 bg-black/85 p-4">
                  {/* ── Indication équation ── */}
                  <div className="mb-3 rounded-lg border border-slate-600/40 bg-slate-700/30 px-3 py-2 text-[11px] text-slate-300 italic">
                    ℹ️ Le résultat (183) est correct — ne pas le modifier. Seul l'ordre de deux chiffres à gauche pose problème.
                  </div>
                  <DigitSwapEquationControlled title="Ligne de rapprochement" initial={ALPHA_INIT}
                    chars={alphaChars} setChars={setAlphaChars} solved={alphaSolved} setSolved={setAlphaSolved}
                    onSolved={(pair) => setAlphaPair(pair)} onReset={() => setAlphaPair(null)} />
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        {/* Dossier BETA — indication équation ajoutée */}
        <ModalRegister open={showBeta} title="Dossier BETA — Contrôle de conformité" onClose={() => setShowBeta(false)} containerClassName="!max-w-[920px]" onHint={() => setShowHintModal(true)}>
          <div className="py-2">
            <div className="rounded-2xl border border-black/10 bg-[#eef2ff] text-black shadow-[0_25px_80px_rgba(0,0,0,0.45)] overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-black/60">Audit — Validation arithmétique (BETA)</div>
                    <div className="mt-1 text-[12px] text-black/70">Le contrôle automatique signale une anomalie "simple".</div>
                  </div>
                  <div className="text-[11px] font-mono text-black/45">ref: CMP-BETA</div>
                </div>
                <div className="mt-4 rounded-xl border border-black/10 bg-white/70 p-3 text-[12px] text-black/70">
                  <div className="font-semibold text-black/75">Extrait — rapport de conformité</div>
                  <div className="mt-1 leading-relaxed">Une <span className="font-semibold">inversion de deux chiffres</span> a été constatée. Rectification requise avant classement.</div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px] text-black/70">
                  <div className="rounded-xl border border-black/10 bg-white/60 p-3">
                    <div className="font-semibold text-black/75">Référence</div>
                    <div className="mt-2 flex flex-wrap gap-2 items-center">
                      <span className="px-2 py-1 rounded-md border border-black/15 bg-white/70 font-mono text-[11px]">CMP-BETA</span>
                      <span className="px-2 py-1 rounded-md border border-black/15 bg-white/70 font-mono text-[11px]" style={{ transform: "rotate(1.5deg)" }}>tirage 2</span>
                      {betaSolved && <span className="px-2 py-1 rounded-md border border-emerald-600/30 text-[11px]" style={{ background: "rgba(57,255,20,0.18)", boxShadow: "0 0 10px rgba(57,255,20,0.35)" }}>tampon : OK</span>}
                    </div>
                  </div>
                  <div className="rounded-xl border border-black/10 bg-white/60 p-3">
                    <div className="font-semibold text-black/75">Annexe</div>
                    <div className="mt-1">"Synthèse de lot — archivage interne." (page 3/7)</div>
                  </div>
                </div>
                <div className="mt-4 rounded-xl border border-black/10 bg-black/85 p-4">
                  {/* ── Indication équation ── */}
                  <div className="mb-3 rounded-lg border border-slate-600/40 bg-slate-700/30 px-3 py-2 text-[11px] text-slate-300 italic">
                    ℹ️ Le résultat (194) est correct — ne pas le modifier. Seul l'ordre de deux chiffres à gauche pose problème.
                  </div>
                  <DigitSwapEquationControlled title="Synthèse de lot" initial={BETA_INIT}
                    chars={betaChars} setChars={setBetaChars} solved={betaSolved} setSolved={setBetaSolved}
                    onSolved={(pair) => setBetaPair(pair)} onReset={() => setBetaPair(null)} />
                </div>
                <div className="mt-3 text-[11px] text-black/55 italic">(Les chiffres mis en évidence servent de clé.)</div>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showOrderHint} title="Mémo d'archivage — procédure" onClose={() => setShowOrderHint(false)} containerClassName="!max-w-[860px]" onHint={() => setShowHintModal(true)}>
          <div className="py-4">
            <div className="rounded-2xl border border-black/10 bg-[#f6f0e6] text-black shadow-[0_25px_80px_rgba(0,0,0,0.35)] overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-black/60">Archives — note de classement</div>
                <div className="mt-3 rounded-xl border border-black/10 bg-white/65 p-4 text-[12px] text-black/75 leading-relaxed">
                  <div className="font-semibold text-black/80">"Règle de rangement (lots de contrôle)"</div>
                  <div className="mt-2">Les pièces d'un même contrôle sont classées dans l'ordre du <span className="font-semibold">tirage</span> indiqué sur les dossiers.</div>
                  <div className="mt-3 rounded-lg border border-black/10 bg-black/5 p-3">
                    <div className="font-mono text-[12px]">"Tirage 1" précède "Tirage 2". Ne pas inverser lors du classement.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showPostitMural} title="Procédure de sécurité" onClose={() => setShowPostitMural(false)} containerClassName="!max-w-[700px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-3">🔒 PROCÉDURE DE SÉCURITÉ</div>
              <div className="text-sm text-slate-200 leading-relaxed space-y-3">
                <p><span className="font-semibold">Déverrouillage multi-étapes :</span></p>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Collecte des fragments de validation (répartis entre services)</li>
                  <li>Reconstitution du captcha visuel</li>
                  <li>Extraction du mot de passe final</li>
                </ol>
                <p className="text-xs text-slate-500 italic text-right">— Département Sécurité</p>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showTiroir} title="Tiroir — Dossiers administratifs" onClose={() => { setShowTiroir(false); setTiroirFileOpen(null); }} containerClassName="!max-w-[800px]" onHint={() => setShowHintModal(true)}>
          <TiroirContent />
        </ModalRegister>

        <ModalRegister open={showServeur} title="Terminal de diagnostic" onClose={() => setShowServeur(false)} containerClassName="!max-w-[800px]" onHint={() => setShowHintModal(true)}>
          <div className="py-4 px-2">
            <div className="rounded-xl border border-cyan-500/30 bg-black p-5 font-mono text-sm">
              <div className="text-cyan-400 mb-4">[STATUT SYSTÈME]</div>
              <div className="border-t border-b border-cyan-500/30 py-3 mb-4">
                <div className="text-slate-300 space-y-1">
                  <div>Module MIRAGIO : <span className="text-red-400">VERROUILLÉ</span></div>
                  <div className="text-xs text-slate-500">Raison : Validation multi-services requise</div>
                  <div className="mt-2">Fragments collectés : <span className="text-amber-300">{availableLayers.length}/5</span></div>
                </div>
              </div>
              <div className="text-cyan-400 mb-2">[TRACE D'ACCÈS — TEMPS RÉEL]</div>
              <div className="text-slate-300 space-y-1 text-xs mb-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <div key={n} className="flex items-center gap-2">
                    <span className={fragmentStatus[n] ? "text-emerald-400" : "text-slate-600"}>{fragmentStatus[n] ? "●" : "○"}</span>
                    <span>Fragment {n} ({["Archives", "Calibration IA", "Supervision IA", "Communications", "Sécurité"][n - 1]})</span>
                    {fragmentStatus[n] && <span className="text-emerald-400 text-[10px]">✓ DISPONIBLE</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showClasseur} title="Registre d'incidents — Archives" onClose={() => setShowClasseur(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <div className="py-4 px-2">
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">📋 Classeur d'incidents archivés</div>
              <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4">
                <div className="text-center mb-3">
                  <div className="text-sm text-cyan-200 my-2">INCIDENT #1337 — 12/08/2023</div>
                </div>
                <div className="text-sm text-slate-200 space-y-2">
                  <p><span className="text-slate-400">Origine :</span> <span className="font-mono">Gratitude_debt_monitor.sys</span></p>
                  <p className="text-xs leading-relaxed"><span className="text-slate-400">Description :</span> ORIGAMI a envoyé 247 notifications "Merci de me remercier" au service RH. L'équipe a dû créer une <span className="italic">cérémonie de gratitude</span> improvisée.</p>
                </div>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 mt-3">
                  <div className="text-xs text-slate-300">"Seuil de gratitude ajusté. Module marqué 'sensible'."</div>
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        {playerCount <= 5 && (
          <ModalRegister open={showToxicPattern} title="Canal #origami-support — Rapport"
            onClose={() => setShowToxicPattern(false)} containerClassName="!max-w-[780px]">
            <ToxicPatternContent />
          </ModalRegister>
        )}

        <CodeEntryModal
          open={showCodeModal} onClose={() => setShowCodeModal(false)}
          expected={expectedBoxCode || "----"} disabled={!expectedBoxCode}
          onSuccess={async () => {
            setBoxUnlockedByCode(true); setShowCodeModal(false);
            missionResolve({ key: "s2.p1.alpha.found" }); missionResolve({ key: "s2.p1.beta.found" });
            try { await openBox(); } catch (e) { console.error(e); }
          }}
          onHint={() => setShowHintModal(true)} setShowHintModal={setShowHintModal}
          onWrongCode={() => trackError({ sessionCode: code, stage: 2, playerId, reason: "s2.p1.box.wrong_code" })}
        />

        <HintsModalHub
          open={showHintModal} onClose={() => setShowHintModal(false)}
          title="Indices — Joueur 1 (Archives)"
          markHintUsedOnce={markHintUsedOnce} categories={hintCategories}
          usedStorageKey={`hints_used:${code}:s2:p${playerId}`}
        />
      </div>
    </div>
  );
}